import { CATEGORIES_FR } from '../constants/categories-fr.js';

/*
 * Libellés d'affichage des valeurs brutes de la source.
 *
 * La source livre des codes (« i_did », « made_to_order », « 2007_2009 ») et
 * une taxonomie en anglais. L'interface, les exports et les rapports passent
 * par ces fonctions : une seule traduction, partagée par l'API et le front.
 */

/** Origine des articles découverts en parcourant les avis d'une boutique. */
export const ORIGINE_VIA_AVIS = 'via_avis';

/** Comment l'article est entré au catalogue : par les avis, ou par une recherche. */
export function libelleOrigine(origine: string | null | undefined): string {
  if (!origine) return 'Origine inconnue';
  if (origine === ORIGINE_VIA_AVIS) return 'Découvert par les avis';
  return `Recherche « ${origine} »`;
}

const QUI_A_FAIT: Readonly<Record<string, string>> = {
  i_did: 'Par la boutique',
  collective: 'Par un collectif',
  someone_else: 'Par un autre artisan',
};

export function libelleQuiAFait(valeur: string | null | undefined): string | null {
  if (!valeur) return null;
  return QUI_A_FAIT[valeur] ?? valeur;
}

/** « made_to_order », « 2007_2009 », « 1990s », « before_1700 » → français. */
export function libelleQuandFait(valeur: string | null | undefined): string | null {
  if (!valeur) return null;
  if (valeur === 'made_to_order') return 'Sur commande';
  const intervalle = /^(\d{4})_(\d{4})$/.exec(valeur);
  if (intervalle) return `Entre ${intervalle[1]} et ${intervalle[2]}`;
  const decennie = /^(\d{4})s$/.exec(valeur);
  if (decennie) return `Années ${decennie[1]}`;
  const avant = /^before_(\d{4})$/.exec(valeur);
  if (avant) return `Avant ${avant[1]}`;
  return valeur;
}

/** « Par la boutique, sur commande ». */
export function libelleFabrication(
  qui: string | null | undefined,
  quand: string | null | undefined,
): string | null {
  const parties = [libelleQuiAFait(qui), libelleQuandFait(quand)].filter((p): p is string =>
    Boolean(p),
  );
  if (!parties.length) return null;
  return parties.map((p, i) => (i === 0 ? p : p.charAt(0).toLowerCase() + p.slice(1))).join(', ');
}

/** Un segment de taxonomie en français ; en anglais s'il n'est pas encore traduit. */
export function libelleSegmentCategorie(segment: string): string {
  return CATEGORIES_FR[segment] ?? segment;
}

/**
 * Chemin de catégorie traduit, séparé par « › ». `niveaux` ne garde que les
 * derniers segments : « Sols et tapis › Tapis » plutôt que le chemin entier.
 * Le chemin anglais reste la clé des filtres ; seul l'affichage est traduit.
 */
export function libelleCategorie(chemin: string | null | undefined, niveaux?: number): string {
  if (!chemin) return '—';
  const segments = chemin.split(' > ').map(libelleSegmentCategorie);
  return (niveaux ? segments.slice(-niveaux) : segments).join(' › ');
}

/**
 * Langue de l'avis, lue comme le marché de l'acheteur.
 *
 * Toujours nommée par ses locuteurs (« Germanophones », pas « Allemand ») :
 * une langue regroupe plusieurs pays, et un nom de langue se lirait comme un
 * pays. « Anglophones » couvre ainsi les États-Unis, le Royaume-Uni, etc.
 */
export const LANGUES_ACHETEURS: Readonly<Record<string, string>> = {
  en: 'Anglophones',
  de: 'Germanophones',
  fr: 'Francophones',
  nl: 'Néerlandophones',
  es: 'Hispanophones',
  it: 'Italophones',
  pt: 'Lusophones',
  ja: 'Japonophones',
  pl: 'Polonophones',
  ru: 'Russophones',
  sv: 'Suédophones',
  da: 'Danophones',
  no: 'Norvégophones',
  fi: 'Finnophones',
  cs: 'Tchécophones',
  hu: 'Magyarophones',
  ar: 'Arabophones',
  ko: 'Coréanophones',
  ca: 'Catalanophones',
  tr: 'Turcophones',
  ro: 'Roumanophones',
  he: 'Hébréophones',
  el: 'Hellénophones',
  uk: 'Ukrainophones',
  zh: 'Sinophones',
  hr: 'Croatophones',
  sr: 'Serbophones',
  bg: 'Bulgarophones',
  sk: 'Slovacophones',
  sl: 'Slovénophones',
  et: 'Estonophones',
  lt: 'Lituanophones',
  lv: 'Lettonophones',
  fa: 'Persanophones',
  vi: 'Vietnamophones',
  sq: 'Albanophones',
  mg: 'Malgachophones',
  ln: 'Lingalaphones',
  sw: 'Swahiliphones',
  is: 'Islandophones',
  mt: 'Maltophones',
  lb: 'Luxembourgophones',
  eu: 'Bascophones',
  oc: 'Occitanophones',
  br: 'Brittophones',
  co: 'Corsophones',
  af: 'Afrikaanophones',
  so: 'Somalophones',
  hy: 'Arménophones',
  az: 'Azérophones',
  be: 'Biélorussophones',
  mk: 'Macédonophones',
  hi: 'Hindiphones',
  ur: 'Ourdouphones',
  bn: 'Bengalophones',
  ta: 'Tamoulophones',
  ha: 'Haoussaphones',
  wo: 'Wolophones',
};

/**
 * Anciens codes et doublons, ramenés au code qui porte le libellé : la source
 * rend encore `iw` pour l'hébreu, et `nb` comme `no` pour le norvégien.
 */
const ALIAS_LANGUES: Readonly<Record<string, string>> = {
  iw: 'he',
  in: 'id',
  ji: 'yi',
  jw: 'jv',
  mo: 'ro',
  nb: 'no',
  nn: 'no',
  tl: 'fil',
};

/**
 * Code de langue d'un avis, ramené à la langue seule : `pt-PT` et `pt` sont un
 * même marché, comme `ar-Latn` et `ar`. Sans quoi chaque variante d'écriture
 * fait une ligne de plus, et deux fois le même nom dans les listes. Un code
 * vide vaut une langue inconnue. Même règle en SQL dans la migration
 * `20261007090000_langues_avis`.
 */
export function normaliserLangue(code: string | null | undefined): string | null {
  const racine = code?.trim().toLowerCase().split(/[-_]/)[0];
  if (!racine) return null;
  return ALIAS_LANGUES[racine] ?? racine;
}

let nomsDeLangues: Intl.DisplayNames | null | undefined;

/** Nom français d'une langue sans libellé de locuteurs, s'il est connu. */
function nomDeLangue(code: string): string | null {
  if (nomsDeLangues === undefined) {
    try {
      nomsDeLangues = new Intl.DisplayNames(['fr'], { type: 'language', fallback: 'none' });
    } catch {
      nomsDeLangues = null;
    }
  }
  try {
    const nom = nomsDeLangues?.of(code);
    return nom ? nom.charAt(0).toUpperCase() + nom.slice(1) : null;
  } catch {
    // Code mal formé : `of` lève une RangeError.
    return null;
  }
}

/**
 * Libellé d'une langue d'acheteurs. Une langue rare, sans nom de locuteurs
 * usuel, garde son nom suivi de « (langue) » : « Hawaïen » seul se lirait
 * comme une nationalité. Une langue que le navigateur ne sait pas nommer
 * (`mh`, le marshallais) se dit « Autre langue (mh) », jamais par son code seul.
 */
export function libelleLangue(code: string | null | undefined): string {
  const langue = normaliserLangue(code);
  if (!langue || langue === 'und') return 'Indéterminé';
  const locuteurs = LANGUES_ACHETEURS[langue];
  if (locuteurs) return locuteurs;
  const nom = nomDeLangue(langue);
  return nom ? `${nom} (langue)` : `Autre langue (${langue})`;
}

// ── Tailles des variantes ──────────────────────────────────────────────────

export interface TailleAnalysee {
  largeurCm: number;
  longueurCm: number;
  surfaceM2: number;
}

const NOMBRE = '(\\d+(?:[.,]\\d+)?)';
const PAIRE = `${NOMBRE}\\s*[x×*]\\s*${NOMBRE}`;
const EN_CM = new RegExp(`${PAIRE}\\s*cm`, 'i');
const EN_PIEDS = new RegExp(`${PAIRE}\\s*(?:ft|feet|foot|pieds?|')`, 'i');
const EN_METRES = new RegExp(`${PAIRE}\\s*m(?![a-z])`, 'i');

const nombre = (texte: string) => Number(texte.replace(',', '.'));

/**
 * Dimensions lues dans un libellé de variante, en centimètres.
 *
 * « Size: 3x5 ft = 92x153 cm | Fringes: Without » donne 92 × 153 : la mesure
 * en centimètres prime, puis les pieds (30,48 cm), puis les mètres. Rend
 * `null` quand le libellé ne décrit pas deux dimensions.
 */
export function analyserTaille(libelle: string | null | undefined): TailleAnalysee | null {
  if (!libelle) return null;
  let facteur = 1;
  let trouve = EN_CM.exec(libelle);
  if (!trouve) {
    trouve = EN_PIEDS.exec(libelle);
    facteur = 30.48;
  }
  if (!trouve) {
    trouve = EN_METRES.exec(libelle);
    facteur = 100;
  }
  if (!trouve) return null;
  const a = nombre(trouve[1]) * facteur;
  const b = nombre(trouve[2]) * facteur;
  if (!(a > 0 && b > 0)) return null;
  const [largeurCm, longueurCm] = a <= b ? [a, b] : [b, a];
  return {
    largeurCm: Math.round(largeurCm),
    longueurCm: Math.round(longueurCm),
    surfaceM2: Math.round((largeurCm / 100) * (longueurCm / 100) * 100) / 100,
  };
}

export interface PointTaille extends TailleAnalysee {
  prix: number;
  /** Libellé de la première variante qui porte cette taille. */
  libelle: string;
}

/**
 * Une entrée par taille distincte, au prix le plus bas de ses variantes.
 * Les options qui ne changent pas la taille (franges, couleur) se replient :
 * 144 variantes d'un tapis donnent ses 48 tailles.
 */
export function grilleTailles(
  variantes: readonly { libelle: string | null; prix: number | null }[],
): PointTaille[] {
  const parTaille = new Map<string, PointTaille>();
  for (const v of variantes) {
    const taille = analyserTaille(v.libelle);
    if (!taille || v.prix === null || !(v.prix > 0)) continue;
    const cle = `${taille.largeurCm}x${taille.longueurCm}`;
    const existant = parTaille.get(cle);
    if (!existant || v.prix < existant.prix) {
      parTaille.set(cle, { ...taille, prix: v.prix, libelle: v.libelle ?? cle });
    }
  }
  return [...parTaille.values()].sort((a, b) => a.surfaceM2 - b.surfaceM2 || a.prix - b.prix);
}

/**
 * Anomalie de grille : une taille nettement plus grande vendue nettement moins
 * cher qu'une plus petite. Rend la plus marquée, ou `null`. Les écarts de
 * moins de 5 % sur la surface ou le prix sont ignorés : arrondis de vendeur.
 */
export function anomalieGrille(
  points: readonly PointTaille[],
): { grande: PointTaille; petite: PointTaille } | null {
  let pire: { grande: PointTaille; petite: PointTaille; ecart: number } | null = null;
  for (const grande of points) {
    for (const petite of points) {
      if (grande.surfaceM2 < petite.surfaceM2 * 1.05) continue;
      if (grande.prix * 1.05 > petite.prix) continue;
      const ecart = petite.prix / grande.prix;
      if (!pire || ecart > pire.ecart) pire = { grande, petite, ecart };
    }
  }
  return pire ? { grande: pire.grande, petite: pire.petite } : null;
}
